import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  // Helper function to check if a link is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1F11ED]/95 backdrop-blur-xl border-b border-white/20 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <ReceevitLogoCompact size={160} forBlueBackground={true} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`transition-colors duration-200 font-medium ${
                isActive('/') 
                  ? 'text-blue-200 bg-white/10 px-3 py-2 rounded-lg' 
                  : 'text-white hover:text-blue-200'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/features" 
              className={`transition-colors duration-200 font-medium ${
                isActive('/features') 
                  ? 'text-blue-200 bg-white/10 px-3 py-2 rounded-lg' 
                  : 'text-white hover:text-blue-200'
              }`}
            >
              Features
            </Link>
            <Link 
              to="/pricing" 
              className={`transition-colors duration-200 font-medium ${
                isActive('/pricing') 
                  ? 'text-blue-200 bg-white/10 px-3 py-2 rounded-lg' 
                  : 'text-white hover:text-blue-200'
              }`}
            >
              Pricing
            </Link>
            <Link 
              to="/about" 
              className={`transition-colors duration-200 font-medium ${
                isActive('/about') 
                  ? 'text-blue-200 bg-white/10 px-3 py-2 rounded-lg' 
                  : 'text-white hover:text-blue-200'
              }`}
            >
              About
            </Link>
            <Link 
              to="/support" 
              className={`transition-colors duration-200 font-medium ${
                isActive('/support') 
                  ? 'text-blue-200 bg-white/10 px-3 py-2 rounded-lg' 
                  : 'text-white hover:text-blue-200'
              }`}
            >
              Support
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/login"
              className="text-white hover:text-blue-200 transition-colors duration-200 font-medium px-4 py-2 rounded-lg hover:bg-white/10"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="bg-white text-[#1F11ED] px-6 py-2 rounded-xl font-semibold hover:bg-blue-50 hover:shadow-lg transition-all duration-300"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-white hover:text-blue-200 transition-colors duration-200"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#1F11ED]/95 border-t border-white/20"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className={`block px-3 py-2 transition-colors duration-200 font-medium rounded-lg ${
                  isActive('/') 
                    ? 'text-blue-200 bg-white/10' 
                    : 'text-white hover:text-blue-200'
                }`}
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/features"
                className={`block px-3 py-2 transition-colors duration-200 font-medium rounded-lg ${
                  isActive('/features') 
                    ? 'text-blue-200 bg-white/10' 
                    : 'text-white hover:text-blue-200'
                }`}
                onClick={() => setIsOpen(false)}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className={`block px-3 py-2 transition-colors duration-200 font-medium rounded-lg ${
                  isActive('/pricing') 
                    ? 'text-blue-200 bg-white/10' 
                    : 'text-white hover:text-blue-200'
                }`}
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/about"
                className={`block px-3 py-2 transition-colors duration-200 font-medium rounded-lg ${
                  isActive('/about') 
                    ? 'text-blue-200 bg-white/10' 
                    : 'text-white hover:text-blue-200'
                }`}
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              <Link
                to="/support"
                className={`block px-3 py-2 transition-colors duration-200 font-medium rounded-lg ${
                  isActive('/support') 
                    ? 'text-blue-200 bg-white/10' 
                    : 'text-white hover:text-blue-200'
                }`}
                onClick={() => setIsOpen(false)}
              >
                Support
              </Link>
              <div className="pt-4 space-y-2">
                <Link
                  to="/login"
                  className="block px-3 py-2 text-white hover:text-blue-200 transition-colors duration-200 font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="block px-3 py-2 bg-white text-[#1F11ED] rounded-lg font-semibold text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
